import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useSlug } from "@/hooks/useSlug";
import { documentMutations } from "@/queries/documents/mutations";
import { useDocumentQueries } from "@/queries/documents/queryOptions";
import { routes } from "@/routes";

import {
  type CreateOrEditFolderFormValues,
  DocumentFolderForm,
  type DocumentFolderFormProps,
} from "./DocumentFolderForm";
import { getDefaultFormValues } from "./Manager";

export function EditDocumentFolderPage(): React.ReactNode {
  const slug = useSlug();
  const { folderId: parentFolderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();

  const editFolderMutation = documentMutations.useUpdateFolder(parentFolderId!);

  const documentQueries = useDocumentQueries();
  const {
    data: folderDetails,
    isLoading: folderDetailsIsLoading,
    error: folderDetailsError,
  } = useQuery({
    ...documentQueries.folderDetails({
      folderId: parentFolderId!,
    }),
    enabled: true,
  });
  const {
    data: parentFolder,
    isPending: parentFolderIsPending,
    error: parentFolderError,
  } = useQuery({
    ...documentQueries.listDocuments({
      folderId: parentFolderId!,
    }),
    enabled: !!parentFolderId,
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        id: data.id,
        name: data.name,
      };
    },
  });
  const defaultFormValues = getDefaultFormValues(parentFolderId!, folderDetails);
  function onSubmit(data: CreateOrEditFolderFormValues) {
    const request = {
      name: data.name,
      nameTranslations: [],
      parentFolderId: data.parentFolderId,
    };

    return editFolderMutation.mutate(request, {
      onSuccess: () => {
        void navigate(routes.documents.documentsList({ slug, folderId: parentFolderId! }));
      },
    });
  }

  const error = folderDetailsError || parentFolderError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  if (folderDetailsIsLoading || parentFolderIsPending) {
    return <FullSizeLoader withPadding />;
  }

  const formProps: DocumentFolderFormProps = {
    folderId: parentFolder.id,
    folderName: parentFolder?.name,
    onSubmit,
    originalLanguageId: folderDetails?.originalLanguageId,
    defaultFormValues,
    isEditMode: true,
    isSubmitting: editFolderMutation.isPending,
  };

  return <DocumentFolderForm {...formProps} />;
}
